import React, { useState, useEffect } from "react";
import { signOut } from "./core/api/authApi";
import useSession from "./core/hooks/useSession";
import useMyAccount, { AccountContext } from "./core/hooks/useAccount";
import useJobManager from "./core/hooks/useJobManager";
import AppShell from "./core/layout/AppShell";
import { parseReportAddress } from "./core/data/reportAddress";
import Login from "./features/auth/Login";
import NotLinked from "./features/auth/NotLinked";
import useScanForm from "./features/search/useScanForm";
import SearchScreen from "./features/search/SearchScreen";
import ScanningScreen from "./features/scanning/ScanningScreen";
import JobsScreen from "./features/jobs/JobsScreen";
import ReportScreen from "./features/report/ReportScreen";

// The job named in the page address when the app first loads (a refresh or a
// copied report link), or null.
const ADDRESS_JOB = parseReportAddress(window.location.search).jobId;

// Sign-in gate, then the login's account (agency), then one of four screens:
// input, scanning, myJobs, report. Every screen only sees that account's
// scans (AccountContext; the database rules enforce it too).
// A report's job, tab, filters and pin live in the page address (?job=...),
// so a refresh reopens the same report. "opening" = loading that job.
export default function AdAudit() {
  const { session, authLoading } = useSession();
  const { account, accountLoading } = useMyAccount(session);
  const [screen, setScreen] = useState(ADDRESS_JOB ? "opening" : "input");
  const [jobSearch, setJobSearch] = useState("");
  const form = useScanForm();
  const jobManager = useJobManager({ screen, setScreen, agencyId: account ? account.agencyId : null });
  const { openJobById } = jobManager;

  // Reopen the report named in the address, once signed in. Another
  // account's scan isn't found, so it opens New scan instead.
  useEffect(() => {
    if (!account || screen !== "opening") return;
    openJobById(ADDRESS_JOB).then(found => { if (!found) setScreen("input"); });
  }, [account, screen, openJobById]);

  // Leaving the report clears its address, so the next job opens fresh.
  useEffect(() => {
    if (screen !== "report" && screen !== "opening" && window.location.search) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [screen]);

  const goToMyJobs = () => {
    setScreen("myJobs");
    jobManager.refreshJobs();
  };

  // "+ New Scan" keeps whatever is on the search page.
  const newScan = () => {
    setScreen("input");
    jobManager.clearActiveJob();
  };

  const reportJob = jobManager.reportJob;

  if (authLoading || accountLoading) {
    return (
      <div className="min-h-screen bg-violet-50/[0.15] flex items-center justify-center">
        <p className="text-sm text-slate-400">Loading...</p>
      </div>
    );
  }

  if (!session) {
    return <Login />;
  }

  if (!account) {
    return <NotLinked email={session.user.email} onSignOut={() => signOut()} />;
  }

  return (
    <AccountContext.Provider value={account}>
      <AppShell
        fullHeight={screen === "input" || screen === "report" || screen === "opening" || screen === "myJobs"}
        active={screen === "input" ? "newScan" : screen === "scanning" ? null : "jobs"}
        accountName={account.name}
        onMyJobs={goToMyJobs}
        onNewScan={newScan}
        onSignOut={() => signOut()}
      >
        {screen === "opening" && <div className="h-full flex items-center justify-center text-sm text-slate-400">Loading snapshot…</div>}
        {screen === "input" && <SearchScreen form={form} userId={session.user.id} onStart={jobManager.startJob} onOpenScans={goToMyJobs} />}
        {screen === "scanning" && <ScanningScreen activeJob={jobManager.activeJob} recentAppearances={jobManager.recentAppearances} onStop={jobManager.stopActiveJob} />}
        {screen === "myJobs" && (
          <JobsScreen
            agencyId={account.agencyId} jobs={jobManager.jobs} jobsLoading={jobManager.jobsLoading} jobSearch={jobSearch} setJobSearch={setJobSearch}
            onOpenJob={jobManager.openJob} onOpenPinned={jobManager.openJobPinned} onStopJob={jobManager.stopListedJob} onNewScan={newScan}
          />
        )}
        {screen === "report" && <ReportScreen key={reportJob?.id} job={reportJob} />}
      </AppShell>
    </AccountContext.Provider>
  );
}
